import { ClickAwayListener, Paper, Popper } from '@material-ui/core';
import { Editor } from '@tiptap/core';
import { Button, Icon } from 'copilot-design-system';
import { Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { TextField } from 'src/components/TextField';
import { URLValidationRegex } from 'src/constants';
import { UrlUtils } from 'src/utils';

const linkSchema = z.object({
  displayText: z.string().optional(),
  linkUrl: z
    .string()
    .regex(URLValidationRegex, 'Please enter a valid URL')
    .or(z.literal('')),
});

type LinkFormValues = z.infer<typeof linkSchema>;

interface LinkSelectionPopoverProps {
  editor: Editor;
  isOpen: boolean;
  onClose: () => void;
}

export const LinkSelectionPopover = ({
  editor,
  isOpen,
  onClose,
}: LinkSelectionPopoverProps) => {
  const [hasSelection, setHasSelection] = useState(false);
  const [existingUrl, setExistingUrl] = useState('');
  const [existingText, setExistingText] = useState('');
  const displayTextInputRef = useRef<HTMLInputElement>(null);
  const linkUrlInputRef = useRef<HTMLInputElement>(null);

  // Create a virtual anchor element based on cursor position
  const virtualAnchor = useMemo(() => {
    if (!isOpen || !editor) return null;

    const { from, to } = editor.state.selection;
    const start = editor.view.coordsAtPos(from);
    const end = editor.view.coordsAtPos(to);
    const selectionHasText = from !== to;

    const left = selectionHasText ? (start.left + end.right) / 2 : start.left;
    const { top } = start;

    return {
      getBoundingClientRect: () => ({
        top,
        left,
        bottom: top,
        right: left,
        width: 0,
        height: 0,
        x: left,
        y: top,
        toJSON: () => ({}),
      }),
      clientWidth: 0,
      clientHeight: 0,
    };
  }, [isOpen, editor]);

  // Calculate selection state and existing URL/text when popover opens
  useEffect(() => {
    if (isOpen && editor) {
      const { from, to } = editor.state.selection;
      const selectionHasText = from !== to;

      setHasSelection(selectionHasText);
      setExistingUrl(editor.getAttributes('link').href ?? '');

      // Get the link text if cursor is on a link
      if (editor.isActive('link')) {
        const { from: linkFrom, to: linkTo } = editor.state.selection;
        const $from = editor.state.doc.resolve(linkFrom);
        const linkMark = $from
          .marks()
          .find((mark) => mark.type.name === 'link');
        if (linkMark) {
          let markFrom = linkFrom;
          let markTo = linkTo;
          editor.state.doc.nodesBetween(
            Math.max(0, linkFrom - 100),
            Math.min(editor.state.doc.content.size, linkTo + 100),
            (node, pos) => {
              if (node.isText && node.marks.some((m) => m.eq(linkMark))) {
                markFrom = Math.min(markFrom, pos);
                markTo = Math.max(markTo, pos + node.nodeSize);
              }
            },
          );
          const linkText = editor.state.doc.textBetween(markFrom, markTo);
          setExistingText(linkText);
        }
      } else {
        setExistingText('');
      }

      // Focus the appropriate input after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (selectionHasText) {
          linkUrlInputRef.current?.focus();
        } else {
          displayTextInputRef.current?.focus();
        }
      }, 0);
    } else {
      setHasSelection(false);
      setExistingUrl('');
      setExistingText('');
    }
  }, [isOpen, editor]);

  const handleSubmit = ({ displayText, linkUrl }: LinkFormValues) => {
    if (linkUrl === '') {
      // Remove link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (existingUrl && displayText) {
      // Editing existing link: replace text and update URL
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .deleteSelection()
        .insertContent(
          `<a href="${UrlUtils.GetFullUrl(linkUrl)}">${displayText}</a>`,
        )
        .unsetMark('link')
        .run();
    } else if (!hasSelection && displayText) {
      // No selection, no existing link: insert new link with display text
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${UrlUtils.GetFullUrl(linkUrl)}">${displayText}</a>`,
        )
        .unsetMark('link')
        .run();
    } else {
      // Has selection: set link on selected text
      const { to } = editor.state.selection;
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: UrlUtils.GetFullUrl(linkUrl) })
        .setTextSelection(to)
        .unsetMark('link')
        .run();
    }
    onClose();
  };

  if (!virtualAnchor) return null;

  const initialValues: LinkFormValues = {
    displayText: existingText,
    linkUrl: existingUrl,
  };

  return (
    <Popper
      open={isOpen}
      anchorEl={virtualAnchor}
      placement="top"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ]}
      className="z-modal"
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          onClick={(e) => e.stopPropagation()}
          className="border border-solid border-secondary-hover shadow-dropdown-menu p-3 w-64 rounded-md"
        >
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={toFormikValidationSchema(linkSchema)}
            validateOnBlur
            validateOnChange
            enableReinitialize
          >
            {({
              errors,
              handleBlur,
              handleChange,
              submitForm,
              touched,
              values,
            }) => (
              <div className="flex flex-row gap-3">
                <div className="flex flex-col gap-2">
                  {!hasSelection && (
                    <TextField
                      inputRef={displayTextInputRef}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Icon icon="Text" className="h-4 w-4" />
                        ),
                      }}
                      className="h-8 text-lg"
                      placeholder="Text to display"
                      name="displayText"
                      value={values.displayText ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={
                        touched.displayText && errors.displayText
                          ? errors.displayText
                          : undefined
                      }
                    />
                  )}
                  <TextField
                    inputRef={linkUrlInputRef}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Icon icon="Link" className="h-4 w-4" />,
                    }}
                    placeholder="Type or paste a link"
                    name="linkUrl"
                    value={values.linkUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      touched.linkUrl && errors.linkUrl
                        ? errors.linkUrl
                        : undefined
                    }
                  />
                </div>

                <Button
                  className="h-8 self-end"
                  disabled={
                    !values.linkUrl.trim() ||
                    (!hasSelection && !values.displayText?.trim())
                  }
                  variant="text"
                  label={existingUrl ? 'Save' : 'Add'}
                  onClick={() => void submitForm()}
                />
              </div>
            )}
          </Formik>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
